import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  IconButton,
  Alert,
  Chip,
  Divider,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
// Add this import for the back icon:
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachmentOutlinedIcon from '@mui/icons-material/AttachmentOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

import { createHrRequest, showHrRequest, updateHrRequest } from '../../services/allRequestsApi';
import { fetchRequestWorkflowGuides, simulatePublishedRequestWorkflow } from '../../services/requestWorkflowsApi';
import api from '../../services/api';
import { PageHeader, PageLayout, SectionCard, WorkflowGuideCard } from '../../components/ui';
import {
  canAuthorPreApprovalRequest,
  getLocalEmployeeCode,
  getLocalRole,
  isHrTeamRole,
} from './components/requestsUiUtils';
import WorkflowPathPreview from './components/WorkflowPathPreview';
import ForbiddenScreen from '../../components/Common/ForbiddenScreen';

const requestTypeLabels = {
  to_leave: 'Request to Leave',
  loans: 'Loans',
  finance_claim: 'Finance Claim',
  misc: 'Miscellaneous',
  business_trip: 'Business Trip',
  bank: 'Bank',
  resignation: 'Resignation',
  personal_data_change: 'Personal Data Change',
  missing_punch: 'Missing Punch',
  pre_approval: 'Pre-Approval Request',
};

const businessTripTicketDemandOptions = [
  { value: 'company_ticket', label: 'Company Ticket' },
  { value: 'self_booked', label: 'Self Booked' },
  { value: 'ticket_only', label: 'Ticket Only' },
  { value: 'accommodation_only', label: 'Accommodation Only' },
];

const businessTripTypeOptions = [
  { value: 'client_visit', label: 'Client Visit' },
  { value: 'site_visit', label: 'Site Visit' },
  { value: 'training', label: 'Training' },
  { value: 'conference', label: 'Conference' },
];

const businessTripTravelMethodOptions = [
  { value: 'air', label: 'Air' },
  { value: 'road', label: 'Road' },
  { value: 'sea', label: 'Sea' },
  { value: 'mixed', label: 'Mixed' },
];

const businessTripDestinationOptionsByRegion = {
  gcc: [
    { value: 'saudi_arabia', label: 'Saudi Arabia' },
    { value: 'united_arab_emirates', label: 'United Arab Emirates' },
    { value: 'qatar', label: 'Qatar' },
    { value: 'kuwait', label: 'Kuwait' },
    { value: 'bahrain', label: 'Bahrain' },
    { value: 'oman', label: 'Oman' },
  ],
  non_gcc: [
    { value: 'asia', label: 'Asia' },
    { value: 'europe', label: 'Europe' },
    { value: 'africa', label: 'Africa' },
    { value: 'north_america', label: 'North America' },
    { value: 'south_america', label: 'South America' },
    { value: 'australia', label: 'Australia' },
  ],
};

const fieldErrorAliases = {
  leaveType: ['leave_type'],
  fromDate: ['from_date'],
  fromTime: ['from_time'],
  toDate: ['to_date'],
  toTime: ['to_time'],
  loanDate: ['loan_date'],
  noOfPayments: ['no_of_payments'],
  loanAmount: ['amount'],
  loanReason: ['description'],
  requestedItem: ['requested_item'],
  busTripRegion: ['business_trip_region'],
  busTripReason: ['description'],
  busTripNotes: ['notes'],
  bankName: ['bank_name'],
  bankBranch: ['bank_branch'],
  newAccountNumber: ['new_account_number'],
  newIbanNumber: ['new_iban_number'],
  swiftCode: ['swift_code'],
  bankNotes: ['bank_notes'],
  personalDataField: ['personal_data_field'],
  personalDataValue: ['personal_data_value'],
};

function countValidationMessages(errors = {}) {
  return Object.values(errors || {}).reduce((total, value) => {
    if (!value) return total;
    if (Array.isArray(value)) {
      return total + value.filter(Boolean).length;
    }
    return total + 1;
  }, 0);
}

const requestTypeWorkflowMap = {
  to_leave: [
    { actor: 'Employee', action: 'Submit leave request.', note: 'Status starts as pending.' },
    { actor: 'Manager', action: 'Approve or reject request.' },
    { actor: 'HR Admin', action: 'Final approval workflow step.' },
  ],
  misc: [
    { actor: 'Employee', action: 'Submit request with required details.' },
    { actor: 'Manager', action: 'Approve or reject request.' },
    { actor: 'HR Admin', action: 'Final approval workflow step.' },
  ],
  business_trip: [
    { actor: 'Employee', action: 'Submit trip request with dates and reason.' },
    { actor: 'Manager', action: 'Approve or reject request.' },
    { actor: 'HR Admin', action: 'Final approval workflow step.' },
  ],
  resignation: [
    { actor: 'Employee', action: 'Submit resignation details and dates.' },
    { actor: 'Manager', action: 'Review and approve/reject.' },
    { actor: 'HR Admin', action: 'Finalize resignation processing.' },
  ],
  personal_data_change: [
    { actor: 'Employee', action: 'Submit requested profile field changes.' },
    { actor: 'Manager', action: 'Approve or reject request.' },
    { actor: 'HR Admin', action: 'Validate and apply approved changes.' },
  ],
  bank: [
    { actor: 'Employee', action: 'Submit bank update request.' },
    { actor: 'HR Admin', action: 'Approve or reject and update banking profile.' },
  ],
  missing_punch: [
    { actor: 'Employee', action: 'Submit missed attendance punch details.' },
    { actor: 'HR Admin', action: 'Validate and approve or reject the correction.' },
  ],
  finance_claim: [
    { actor: 'Employee', action: 'Submit expense claim rows and attachments.' },
    { actor: 'HR Admin', action: 'First approval step.' },
    { actor: 'Finance Coordinator', action: 'Coordinator review for finance claims.' },
    { actor: 'Finance', action: 'Final finance approval or rejection.' },
  ],
  loans: [
    { actor: 'Employee', action: 'Submit loan request and repayment info.' },
    { actor: 'HR Admin', action: 'First approval step.' },
    { actor: 'Finance', action: 'Financial review and approval/rejection.' },
    { actor: 'CEO', action: 'Final decision for loan requests.' },
  ],
  pre_approval: [
    { actor: 'HR Team', action: 'Create and submit pre-approval request.' },
    { actor: 'HR Manager', action: 'First review step before department approval.' },
    { actor: 'Head of Department (selected department)', action: 'Review and approve/reject, including billing inputs.' },
    { actor: 'Operations Manager', action: 'Review and approve/reject.' },
    { actor: 'CEO', action: 'Review and approve/reject.' },
    { actor: 'Executive Managing Director', action: 'Final approval decision.' },
  ],
};

function normalizePreApprovalRoutingValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function isBusinessDevelopmentDepartment(department) {
  if (!department) return false;

  const candidates = [
    normalizePreApprovalRoutingValue(department?.code),
    normalizePreApprovalRoutingValue(department?.name),
  ].filter(Boolean);

  return candidates.some(
    (candidate) =>
      ['bd', 'business_development', 'businessdevelopment'].includes(candidate) ||
      candidate.includes('business_development') ||
      candidate.includes('businessdevelopment'),
  );
}

function isPmcDepartment(department) {
  if (!department) return false;

  const candidates = [
    normalizePreApprovalRoutingValue(department?.code),
    normalizePreApprovalRoutingValue(department?.name),
  ].filter(Boolean);

  return candidates.some(
    (candidate) => candidate === 'pmc' || candidate.includes('pmc'),
  );
}

function hasSharedPreApprovalApprover(loadedRequest) {
  const hodCode = String(loadedRequest?.pre_approval_hod_code || '').trim();
  const operationsCode = String(loadedRequest?.pre_approval_operations_code || '').trim();

  return Boolean(hodCode && operationsCode && hodCode === operationsCode);
}

function getPreApprovalWorkflowSteps(skipHod) {
  const steps = requestTypeWorkflowMap.pre_approval || [];
  return skipHod ? steps.filter((step) => step.actor !== 'Head of Department (selected department)') : steps;
}

function getPreApprovalRevisionTargetDisplay(requestData) {
  const targetStep = String(requestData?.pre_approval_revision_target_step || '').toLowerCase();
  const targetLabel = String(requestData?.pre_approval_revision_target_label || '').trim();
  const targetName =
    targetStep === 'hr_team_creator'
      ? String(requestData?.user?.name || requestData?.requester_name || requestData?.prepared_by_name || '').trim()
      : targetStep === 'hod'
        ? String(requestData?.pre_approval_hod_name || '').trim()
        : '';

  if (targetName && targetLabel) return `${targetName} (${targetLabel})`;
  return targetName || targetLabel || 'N/A';
}

function isUploadValue(value) {
  return (
    (typeof File !== 'undefined' && value instanceof File) ||
    (typeof Blob !== 'undefined' && value instanceof Blob)
  );
}

function sanitizeWorkflowSampleValue(value) {
  if (isUploadValue(value)) return undefined;
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeWorkflowSampleValue(item))
      .filter((item) => item !== undefined);
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, item]) => {
      const cleanValue = sanitizeWorkflowSampleValue(item);
      if (cleanValue !== undefined) acc[key] = cleanValue;
      return acc;
    }, {});
  }
  return value;
}

function compactWorkflowSample(sample) {
  return Object.entries(sample).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === '') return acc;
    if (Array.isArray(value) && value.length === 0) return acc;
    acc[key] = value;
    return acc;
  }, {});
}

function sumFinanceClaimAmounts(claims) {
  return (Array.isArray(claims) ? claims : []).reduce((total, item) => {
    const amount = Number(item?.amount || 0);
    return Number.isFinite(amount) ? total + amount : total;
  }, 0);
}

function buildWorkflowSampleData(formValues, departments = []) {
  const cleanedFormValues = sanitizeWorkflowSampleValue(formValues) || {};
  const selectedPreApprovalDepartment = departments.find(
    (dept) => String(dept.id) === String(formValues.pre_approval_department_id),
  );
  const financeClaimTotal = sumFinanceClaimAmounts(formValues.financeClaims);
  const amount =
    formValues.request_type === 'finance_claim'
      ? (financeClaimTotal ? String(financeClaimTotal) : '')
      : formValues.loanAmount || '';

  return compactWorkflowSample({
    ...cleanedFormValues,
    request_type: formValues.request_type,
    priority: formValues.priority,
    due_date: formValues.due_date,
    notes: formValues.notes,
    amount,
    loan_date: formValues.loanDate,
    loan_period: formValues.noOfPayments,
    no_of_payments: formValues.noOfPayments,
    leave_type: formValues.leaveType,
    from_date: formValues.fromDate || formValues.miscFromDate,
    from_time: formValues.fromTime || formValues.miscFromTime,
    to_date: formValues.toDate || formValues.miscToDate,
    to_time: formValues.toTime || formValues.miscToTime,
    description:
      formValues.reason ||
      formValues.loanReason ||
      formValues.busTripReason ||
      formValues.resignation_reason ||
      formValues.description ||
      '',
    requested_item: formValues.requestedItem,
    is_temporary: formValues.isTemporary ? '1' : '0',
    business_trip_region: formValues.busTripRegion,
    business_trip_ticket_demand: formValues.business_trip_ticket_demand,
    business_trip_type: formValues.business_trip_type,
    business_trip_travel_method: formValues.business_trip_travel_method,
    business_trip_destination: formValues.business_trip_destination,
    bank_name: formValues.bankName,
    bank_branch: formValues.bankBranch,
    bank_transaction_date: formValues.bankTransactionDate,
    personal_data_field: formValues.personalDataField,
    personal_data_value: formValues.personalDataValue,
    candidate_name: formValues.candidate_name,
    position_offered: formValues.position_offered,
    project_client: formValues.project_client,
    pre_approval_department_id: formValues.pre_approval_department_id,
    pre_approval_department_name: selectedPreApprovalDepartment?.name,
    pre_approval_department_code: selectedPreApprovalDepartment?.code,
    package_salary: formValues.package_salary,
    other_cost: formValues.other_cost,
    total_cost_to_company: formValues.total_cost_to_company,
    pre_approval_billing_mode: formValues.pre_approval_billing_mode,
    client_billing: formValues.client_billing,
    marginal_benefit: formValues.marginal_benefit,
    conditional_terms: formValues.conditional_terms,
    conflict_of_interest: formValues.conflict_of_interest,
    missing_punch_date: formValues.missing_punch_date,
    missing_punch_type: formValues.missing_punch_type,
    missing_punch_time: formValues.missing_punch_time,
    missing_punch_project_id: formValues.missing_punch_project_id,
  });
}

function NewHrRequestForm() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: requestId } = useParams();
  const [searchParams] = useSearchParams();
  const currentRole = getLocalRole();
  const currentEmployeeCode = getLocalEmployeeCode();
  // `canCreatePreApproval` keeps the HR-team-only meaning (used to gate
  // commercial fields and HR-side controls). `canSelectPreApproval` is
  // broader: HR team + management roles in the pre-approval chain (HOD,
  // Operations, CEO, Executive). Only those roles can pick the type.
  const canCreatePreApproval = isHrTeamRole(currentRole);
  const canSelectPreApproval = canAuthorPreApprovalRequest(currentRole);
  const localUserName = localStorage.getItem('name') || '';
  const isEditing = Boolean(requestId);

  // Default request_type from query params (e.g. ?type=to_leave).
  const allowedTypes = new Set([
    'to_leave',
    'loans',
    'finance_claim',
    'misc',
    'business_trip',
    'bank',
    'resignation',
    'personal_data_change',
    'missing_punch',
  ]);
  if (canSelectPreApproval || isEditing) {
    allowedTypes.add('pre_approval');
  }
  const typeFromQuery = searchParams.get('type');
  const initialType = allowedTypes.has(typeFromQuery) ? typeFromQuery : 'to_leave';

  // ------------------- Form State -------------------
  const [formValues, setFormValues] = useState({
    request_type: initialType,

    // Common fields
    description: '',
    priority: '',
    due_date: '',
    notes: '',
    attachment: null,

    // to_leave
    leaveType: 'personal', // "personal" | "unpaid" | "work_leave"
    fromDate: '',
    fromTime: '',
    toDate: '',
    toTime: '',
    reason: '',

    // loans
    loanDate: '',
    noOfPayments: '',
    loanAmount: '',
    loanReason: '',

    // finance_claim (multiple expenses)
    financeClaims: [
      {
        transaction_date: '',
        expense_type: '',
        amount: '',
        notes: '',
        attachment: null
      }
    ],

    // misc
    requestedItem: '',
    isTemporary: false,
    miscFromDate: '',
    miscFromTime: '',
    miscToDate: '',
    miscToTime: '',
    miscReason: '',
    miscNotes: '',

    // bank
    bankTransactionDate: '',
    bankName: '',
    bankBranch: 'main',
    newAccountNumber: '',
    newIbanNumber: '',
    swiftCode: '',
    bankNotes: '',

    // business_trip
    busTripRegion: 'gcc', // "gcc" | "non_gcc"
    business_trip_ticket_demand: 'company_ticket',
    business_trip_type: 'client_visit',
    business_trip_travel_method: 'air',
    business_trip_destination: '',
    busTripReason: '',
    busTripNotes: '',
    businessTripAttachments: [],

    // resignation
    resignation_date: '',
    last_working_day: '',
    notice_period: '',
    resignation_reason: '',

    // personal_data_change
    personalDataField: '',
    personalDataValue: '',

    // missing_punch
    missing_punch_date: '',
    missing_punch_type: 'check_in',
    missing_punch_time: '',
    missing_punch_project_id: '',

    // pre_approval
    candidate_name: '',
    position_offered: '',
    project_client: '',
    pre_approval_department_id: '',
    package_salary: '',
    other_cost: '',
    total_cost_to_company: '',
    pre_approval_billing_mode: '',
    client_billing: '',
    marginal_benefit: '',
    conditional_terms: '',
    conflict_of_interest: '',
    requester_name: localUserName,
    prepared_by_name: localUserName,
  });

  // ------------------- Error/Loading State -------------------
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(isEditing);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadedRequest, setLoadedRequest] = useState(null);
  const [requestWorkflowGuides, setRequestWorkflowGuides] = useState({});
  const [workflowPreview, setWorkflowPreview] = useState(null);
  const [workflowPreviewLoading, setWorkflowPreviewLoading] = useState(false);
  const [workflowPreviewError, setWorkflowPreviewError] = useState(null);

  // ------------------- Helper for Field Errors -------------------
  const getFieldError = (fieldName) => {
    const candidateNames = [fieldName, ...(fieldErrorAliases[fieldName] || [])];
    for (const candidate of candidateNames) {
      if (validationErrors[candidate] && validationErrors[candidate].length > 0) {
        return validationErrors[candidate][0];
      }
    }
    return null;
  };

  // Example options for personal_data_change
  const pdOptions = [
    { value: 'name', label: 'Full Name' },
    { value: 'email', label: 'Email Address' },
    { value: 'iqama_no', label: 'Iqama Number' },
    { value: 'position', label: 'Position / Title' },
    { value: 'department', label: 'Department' },
    { value: 'cost_center', label: 'Cost Center' },
    { value: 'site', label: 'Site / Location' },
    { value: 'nationality', label: 'Nationality' },
    { value: 'religion', label: 'Religion' },
    { value: 'gender', label: 'Gender' }
  ];

  // ------------------- Handlers -------------------
  const handleChange = (e, index = null, field = null) => {
    const { name, value, files } = e.target;

    // If updating a row in finance_claim array
    if (index !== null && field) {
      const updatedClaims = [...formValues.financeClaims];
      if (files) {
        updatedClaims[index][field] = files[0];
      } else {
        updatedClaims[index][field] = value;
      }
      setFormValues((prev) => ({ ...prev, financeClaims: updatedClaims }));
      return;
    }

    // Normal field
    if (files) {
      setFormValues((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      if (name === 'request_type') {
        setValidationErrors({});
        setError(null);
      }
      if (name === 'busTripRegion') {
        const destinationOptions = businessTripDestinationOptionsByRegion[value] || [];
        setFormValues((prev) => ({
          ...prev,
          busTripRegion: value,
          business_trip_destination: destinationOptions.some(
            (option) => option.value === prev.business_trip_destination,
          )
            ? prev.business_trip_destination
            : '',
        }));
        return;
      }
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  const isPreApprovalRevision = loadedRequest?.request_type === 'pre_approval'
    && loadedRequest?.pre_approval_status === 'revision_requested';
  const loadedRequestSkipHod = Boolean(loadedRequest?.pre_approval_skip_hod || hasSharedPreApprovalApprover(loadedRequest));
  const rawPreApprovalCurrentStep = String(loadedRequest?.pre_approval_current_step || '').toLowerCase();
  const preApprovalCurrentStep =
    loadedRequestSkipHod && rawPreApprovalCurrentStep === 'hod'
      ? 'operations_manager'
      : rawPreApprovalCurrentStep;
  const hodApprovalStatus = String(loadedRequest?.pre_approval_hod_status || '').toLowerCase();
  const operationsApprovalStatus = String(loadedRequest?.pre_approval_operations_status || '').toLowerCase();
  const ceoApprovalStatus = String(loadedRequest?.pre_approval_ceo_status || '').toLowerCase();
  const revisionTargetStep = String(loadedRequest?.pre_approval_revision_target_step || '').toLowerCase();
  const isHodRevisionEditor = isPreApprovalRevision
    && !loadedRequestSkipHod
    && revisionTargetStep === 'hod'
    && String(loadedRequest?.pre_approval_hod_code || '') === String(currentEmployeeCode || '');
  const isHodWindowEditor =
    loadedRequest?.request_type === 'pre_approval' &&
    !loadedRequestSkipHod &&
    String(loadedRequest?.pre_approval_hod_code || '') === String(currentEmployeeCode || '') &&
    (
      (preApprovalCurrentStep === 'hod' && hodApprovalStatus === 'pending') ||
      (preApprovalCurrentStep === 'operations_manager' && hodApprovalStatus === 'approved' && operationsApprovalStatus === 'pending')
    );
  const isOperationsStepEditor =
    loadedRequest?.request_type === 'pre_approval' &&
    String(loadedRequest?.pre_approval_operations_code || '') === String(currentEmployeeCode || '') &&
    (
      (preApprovalCurrentStep === 'operations_manager' && operationsApprovalStatus === 'pending') ||
      (preApprovalCurrentStep === 'ceo' && operationsApprovalStatus === 'approved' && ceoApprovalStatus === 'pending')
    );
  const canEditPreApprovalCommercialFields =
    formValues.request_type === 'pre_approval' && (isHodRevisionEditor || isHodWindowEditor || isOperationsStepEditor);
  const revisionTargetDisplay = getPreApprovalRevisionTargetDisplay(loadedRequest);

  useEffect(() => {
    let mounted = true;
    fetchRequestWorkflowGuides()
      .then((payload) => {
        if (!mounted) return;
        const rows = Array.isArray(payload?.data) ? payload.data : [];
        const byType = rows.reduce((acc, workflow) => {
          acc[workflow.request_type] = workflow;
          return acc;
        }, {});
        setRequestWorkflowGuides(byType);
      })
      .catch((err) => {
        console.error('Failed to fetch request workflow guides', err);
        if (mounted) setRequestWorkflowGuides({});
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Build the simulate payload once per render, then depend on its JSON
  // signature in the effect below. This prevents the effect from re-running
  // on every keystroke into free-text fields (notes, descriptions) that
  // don't influence routing — without this the simulate call would re-fire
  // hundreds of times during typing and could trigger a render loop.
  const workflowSamplePayload = useMemo(
    () => buildWorkflowSampleData(formValues, departments),
    [formValues, departments],
  );
  const workflowSampleSignature = useMemo(
    () => JSON.stringify(workflowSamplePayload),
    [workflowSamplePayload],
  );

  useEffect(() => {
    if (isEditing) {
      setWorkflowPreview(null);
      setWorkflowPreviewLoading(false);
      setWorkflowPreviewError(null);
      return undefined;
    }

    const requestType = formValues.request_type;
    const workflow = requestWorkflowGuides[requestType];
    const publishedVersion = workflow?.published_version || workflow?.publishedVersion;
    if (!requestType || !publishedVersion?.id) {
      setWorkflowPreview(null);
      setWorkflowPreviewLoading(false);
      setWorkflowPreviewError(null);
      return undefined;
    }

    // Only fire the live simulate when the published graph actually has
    // condition nodes — for linear workflows (start → approval → end) the
    // path is static and WorkflowPathPreview already derives it from the
    // graph. Skipping the simulate avoids a render loop on free-text input
    // (typing notes/reason re-keyed the simulate dep on every keystroke).
    const graphNodes = Array.isArray(publishedVersion?.graph?.nodes)
      ? publishedVersion.graph.nodes
      : [];
    const hasConditionNode = graphNodes.some((n) => (n?.type || '') === 'condition');
    if (!hasConditionNode) {
      setWorkflowPreview(null);
      setWorkflowPreviewLoading(false);
      setWorkflowPreviewError(null);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setWorkflowPreviewLoading(true);
      setWorkflowPreviewError(null);

      simulatePublishedRequestWorkflow(requestType, workflowSamplePayload)
        .then((payload) => {
          if (cancelled) return;
          setWorkflowPreview(payload || null);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error('Failed to simulate published request workflow', err);
          setWorkflowPreview(null);
          setWorkflowPreviewError('Workflow preview is unavailable right now.');
        })
        .finally(() => {
          if (!cancelled) setWorkflowPreviewLoading(false);
        });
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditing,
    formValues.request_type,
    requestWorkflowGuides,
    workflowSampleSignature,
  ]);

  useEffect(() => {
    if (!canCreatePreApproval && !isEditing) return;

    let mounted = true;
    api.get('/hr-requests/pre-approval/departments')
      .then((res) => {
        if (!mounted) return;
        setDepartments(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        console.error('Failed to fetch pre-approval departments', err);
        if (mounted) setDepartments([]);
      });

    return () => {
      mounted = false;
    };
  }, [canCreatePreApproval, isEditing]);

  useEffect(() => {
    if (formValues.request_type !== 'missing_punch') return undefined;

    let mounted = true;
    api.get('/projects')
      .then((res) => {
        if (!mounted) return;
        setProjects(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        console.error('Failed to fetch projects for missing punch request', err);
        if (mounted) setProjects([]);
      });

    return () => {
      mounted = false;
    };
  }, [formValues.request_type]);

  useEffect(() => {
    if (!isEditing) return undefined;

    let mounted = true;
    setBootstrapping(true);

    showHrRequest(requestId)
      .then((data) => {
        if (!mounted) return;
        setLoadedRequest(data || null);
        setFormValues((prev) => ({
          ...prev,
          request_type: data?.request_type || prev.request_type,
          description: data?.description || '',
          priority: data?.priority || '',
          due_date: data?.due_date || '',
          notes: data?.notes || '',
          busTripRegion: data?.business_trip_region || prev.busTripRegion,
          business_trip_ticket_demand: data?.business_trip_ticket_demand || prev.business_trip_ticket_demand,
          business_trip_type: data?.business_trip_type || prev.business_trip_type,
          business_trip_travel_method: data?.business_trip_travel_method || prev.business_trip_travel_method,
          business_trip_destination: data?.business_trip_destination || '',
          busTripReason: data?.description || '',
          busTripNotes: data?.notes || '',
          fromDate: String(data?.from_date || '').slice(0, 10),
          fromTime: String(data?.from_time || '').slice(0, 5),
          toDate: String(data?.to_date || '').slice(0, 10),
          toTime: String(data?.to_time || '').slice(0, 5),
          missing_punch_date: String(data?.missing_punch_date || '').slice(0, 10),
          missing_punch_type: data?.missing_punch_type || prev.missing_punch_type,
          missing_punch_time: String(data?.missing_punch_time || '').slice(0, 5),
          missing_punch_project_id:
            data?.missing_punch_project_id != null ? String(data.missing_punch_project_id) : '',
          candidate_name: data?.candidate_name || '',
          position_offered: data?.position_offered || '',
          project_client: data?.project_client || '',
          pre_approval_department_id:
            data?.pre_approval_department_id != null ? String(data.pre_approval_department_id) : '',
          package_salary: data?.package_salary != null ? String(data.package_salary) : '',
          other_cost: data?.other_cost != null ? String(data.other_cost) : '',
          total_cost_to_company:
            data?.total_cost_to_company != null ? String(data.total_cost_to_company) : '',
          pre_approval_billing_mode: data?.pre_approval_billing_mode || '',
          client_billing: data?.client_billing != null ? String(data.client_billing) : '',
          marginal_benefit: data?.marginal_benefit != null ? String(data.marginal_benefit) : '',
          conditional_terms: data?.conditional_terms || '',
          conflict_of_interest: data?.conflict_of_interest || '',
          requester_name: data?.requester_name || localUserName,
          prepared_by_name: data?.prepared_by_name || localUserName,
        }));
      })
      .catch((err) => {
        console.error('Failed to load request for editing', err);
        if (mounted) {
          setError('Failed to load request for editing.');
        }
      })
      .finally(() => {
        if (mounted) {
          setBootstrapping(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [isEditing, localUserName, requestId]);

  useEffect(() => {
    if (formValues.request_type !== 'pre_approval') return;

    const packageInput = String(formValues.package_salary ?? '').trim();
    const otherInput = String(formValues.other_cost ?? '').trim();
    const nextTotal =
      packageInput === '' && otherInput === ''
        ? ''
        : String(Number(formValues.package_salary || 0) + Number(formValues.other_cost || 0));

    setFormValues((prev) => (
      prev.total_cost_to_company === nextTotal
        ? prev
        : { ...prev, total_cost_to_company: nextTotal }
    ));
  }, [formValues.other_cost, formValues.package_salary, formValues.request_type]);

  const normalizedPreApprovalBillingMode = String(formValues.pre_approval_billing_mode || '').trim().toLowerCase();
  const preApprovalIsBillingHq = normalizedPreApprovalBillingMode === 'billing_hq';
  const preApprovalBillingFieldLabel =
    normalizedPreApprovalBillingMode === 'lumpsum' ? 'Lumpsum Amount' : 'Client Billing (No VAT)';

  useEffect(() => {
    if (!canEditPreApprovalCommercialFields) return;
    if (String(formValues.pre_approval_billing_mode) !== 'billed') return;

    const totalCost = Number(formValues.total_cost_to_company || 0);
    const clientBilling = Number(formValues.client_billing || 0);
    if (!Number.isFinite(totalCost) || !Number.isFinite(clientBilling) || clientBilling <= 0) {
      return;
    }

    const calculatedMargin = ((1 - totalCost / clientBilling) * 100).toFixed(2);
    setFormValues((prev) => (
      prev.marginal_benefit === calculatedMargin
        ? prev
        : { ...prev, marginal_benefit: calculatedMargin }
    ));
  }, [
    canEditPreApprovalCommercialFields,
    formValues.client_billing,
    formValues.pre_approval_billing_mode,
    formValues.total_cost_to_company,
  ]);

  // For toggling the "isTemporary" checkbox in misc
  const handleCheckboxChange = (e) => {
    const { checked, name } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: checked }));
  };

  // Handle multiple files for business trip attachments
  const handleBusinessTripFiles = (e) => {
    const fileList = Array.from(e.target.files || []);
    setFormValues((prev) => ({
      ...prev,
      businessTripAttachments: fileList
    }));
  };

  // Add a new expense row for finance_claim
  const addFinanceClaimRow = () => {
    const newRow = {
      transaction_date: '',
      expense_type: '',
      amount: '',
      notes: '',
      attachment: null
    };
    setFormValues((prev) => ({
      ...prev,
      financeClaims: [...prev.financeClaims, newRow]
    }));
  };

  // Remove an expense row for finance_claim
  const removeFinanceClaimRow = (index) => {
    const updatedClaims = [...formValues.financeClaims];
    updatedClaims.splice(index, 1);
    setFormValues((prev) => ({ ...prev, financeClaims: updatedClaims }));
  };

  // ------------------- Submit -------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});
    setLoading(true);

    if (
      formValues.request_type === 'pre_approval' &&
      canEditPreApprovalCommercialFields
    ) {
      const billingMode = String(formValues.pre_approval_billing_mode || '').trim();
      const billingValue = String(formValues.client_billing || '').trim();
      const marginValue = String(formValues.marginal_benefit || '').trim();

      if (!billingMode) {
        setValidationErrors({
          pre_approval_billing_mode: ['Billing mode is required for commercial review.'],
        });
        setLoading(false);
        return;
      }

      if (billingMode !== 'billing_hq' && !billingValue) {
        setValidationErrors({
          client_billing: ['Billing value is required for commercial review.'],
        });
        setLoading(false);
        return;
      }

      if (billingMode === 'lumpsum' && !marginValue) {
        setValidationErrors({
          marginal_benefit: ['Margin benefit % is required when billing mode is lumpsum.'],
        });
        setLoading(false);
        return;
      }
    }

    try {
      const response = isEditing
        ? await updateHrRequest(requestId, formValues)
        : await createHrRequest(formValues);
      const newId = response?.data?.id || requestId;
      if (newId) {
        navigate(`/hr-requests/${newId}`);
      } else {
        navigate('/all-requests');
      }
    } catch (err) {
      console.error('Error creating request:', err);

      if (err?.response?.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        setError(null);
      } else {
        setValidationErrors({});
        setError('Failed to create request. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Render -------------------
  const { request_type } = formValues;
  const selectedPreApprovalDepartment = departments.find(
    (dept) => String(dept.id) === String(formValues.pre_approval_department_id),
  );
  const selectedDepartmentSkipsHod =
    isBusinessDevelopmentDepartment(selectedPreApprovalDepartment)
    || isPmcDepartment(selectedPreApprovalDepartment);
  const selectedDepartmentChanged =
    isEditing &&
    String(loadedRequest?.pre_approval_department_id ?? '') !== String(formValues.pre_approval_department_id ?? '');
  const loadedRequestSkipsHodRoute =
    !selectedDepartmentChanged &&
    (
      isBusinessDevelopmentDepartment({
        code: loadedRequest?.pre_approval_department_code,
        name: loadedRequest?.pre_approval_department_name,
      }) ||
      isPmcDepartment({
        code: loadedRequest?.pre_approval_department_code,
        name: loadedRequest?.pre_approval_department_name,
      })
    );
  const sharedApproverRoute = !selectedDepartmentChanged && hasSharedPreApprovalApprover(loadedRequest);
  const preApprovalSkipHodWorkflow =
    request_type === 'pre_approval' && (selectedDepartmentSkipsHod || (!selectedDepartmentChanged && loadedRequestSkipHod));
  const selectedWorkflowGuide = requestWorkflowGuides[request_type] || null;
  const selectedPublishedWorkflowVersion =
    selectedWorkflowGuide?.published_version || selectedWorkflowGuide?.publishedVersion || null;
  const hasPublishedWorkflowEngine = Boolean(selectedPublishedWorkflowVersion?.id);
  const configuredWorkflowSteps = (selectedWorkflowGuide?.steps || []).map((step) => ({
    actor: step.label || String(step.step_key || '').replace(/_/g, ' '),
    action: step.description || `Status column: ${step.status_column || 'workflow status'}.`,
  }));
  const workflowSteps =
    request_type === 'pre_approval'
      ? getPreApprovalWorkflowSteps(preApprovalSkipHodWorkflow)
      : (configuredWorkflowSteps.length ? configuredWorkflowSteps : (requestTypeWorkflowMap[request_type] || []));
  const preApprovalDepartmentHint = preApprovalSkipHodWorkflow
    ? 'PMC, Business Development, and shared HOD/Operations routes skip the HOD step and move directly to Operations.'
    : 'Selected department determines the Head of Department approver.';
  const attachmentButtonLabel =
    request_type === 'pre_approval' ? 'Upload Supporting Attachment (Optional)' : 'Upload Attachment (Optional)';
  const attachmentHelperText =
    request_type === 'pre_approval'
      ? 'Upload CV, rate sheet, offer backup, or another supporting file. Accepted formats: PDF, image, Word, or Excel. Max 8 MB.'
      : 'Accepted formats: PDF, image, Word, or Excel. Max 8 MB.';
  const preApprovalApproverMessage = (selectedDepartmentSkipsHod || loadedRequestSkipsHodRoute)
    ? 'PMC and Business Development requests skip HOD and move directly to the Operations Manager step.'
    : sharedApproverRoute
      ? 'This workflow uses one department approver step because HOD and Operations are assigned to the same user.'
      : `Head of Department approver: ${selectedPreApprovalDepartment?.manager?.name || 'Auto-resolved from selected department users.'}`;
  const selectedMissingPunchProject = projects.find(
    (project) => String(project.id) === String(formValues.missing_punch_project_id),
  ) || null;
  const businessTripDestinationOptions =
    businessTripDestinationOptionsByRegion[formValues.busTripRegion] || [];
  const workflowTitle = requestTypeLabels[request_type] || 'Request';
  const validationErrorCount = countValidationMessages(validationErrors);
  const submitLabel =
    loading || bootstrapping
      ? 'Saving...'
      : isEditing
        ? (isPreApprovalRevision ? 'Save & Resubmit' : 'Update Request')
        : 'Create Request';
  const workflowModeLabel = hasPublishedWorkflowEngine && !isEditing ? 'Published workflow' : 'Workflow guide';
  const businessTripAttachmentCount = formValues.businessTripAttachments?.length || 0;
  const summaryRows = [
    { label: 'Type', value: workflowTitle },
    { label: 'Mode', value: isEditing ? 'Editing existing request' : 'New request' },
    { label: 'Workflow', value: workflowModeLabel },
    {
      label: 'Steps',
      value: workflowSteps.length
        ? `${workflowSteps.length} approval ${workflowSteps.length === 1 ? 'step' : 'steps'}`
        : 'No steps configured',
    },
    {
      label: 'Attachment',
      value: formValues.attachment?.name ||
        (businessTripAttachmentCount
          ? `${businessTripAttachmentCount} business trip ${businessTripAttachmentCount === 1 ? 'file' : 'files'}`
      : 'Optional'),
    },
  ];
  const formShellSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
    },
    '& .MuiFormControl-root, & .MuiTextField-root': {
      '& .MuiFormHelperText-root': {
        mx: 0,
      },
    },
    '& .MuiTable-root': {
      '& th': {
        fontSize: '0.72rem',
        fontWeight: 900,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'text.secondary',
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.05),
      },
      '& td': {
        verticalAlign: 'top',
      },
    },
  };
  const requestIntroSx = {
    mb: 1.5,
    mx: { xs: 1.35, sm: 1.7 },
    mt: { xs: 1.35, sm: 1.7 },
    p: { xs: 1.35, sm: 1.6 },
    borderRadius: theme.hr?.radius?.lg || 2,
    border: '1px solid',
    borderColor: alpha(theme.palette.primary.main, 0.16),
    background:
      theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.background.paper, 0.82)})`
        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.background.paper, 0.95)})`,
  };
  const formGridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
    gap: { xs: 1.5, lg: 1.6 },
    alignItems: 'start',
  };
  const summaryPanelSx = {
    position: { lg: 'sticky' },
    top: { lg: 88 },
    p: { xs: 1.25, sm: 1.45 },
    borderRadius: theme.hr?.radius?.lg || 2,
    border: '1px solid',
    borderColor: theme.hr?.line?.soft || alpha(theme.palette.primary.main, 0.12),
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.84 : 0.92),
    boxShadow: theme.hr?.shadow?.soft || `0 14px 34px ${alpha(theme.palette.common.black, 0.08)}`,
  };
  const summaryRowSx = {
    p: 1,
    borderRadius: theme.hr?.radius?.md || 2,
    border: '1px solid',
    borderColor: alpha(theme.palette.primary.main, 0.12),
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.035),
  };
  const stickyActionBarSx = {
    mt: 1.6,
    mx: { xs: -1.35, sm: -1.7 },
    mb: { xs: -1.35, sm: -1.7 },
    px: { xs: 1.35, sm: 1.7 },
    py: 1.25,
    borderTop: '1px solid',
    borderColor: theme.hr?.line?.soft || 'divider',
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.9 : 0.96),
    backdropFilter: 'blur(12px)',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 1,
    flexWrap: 'wrap',
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
  };

  // Editing a submitted request is only supported for pre-approvals (the
  // multi-step approval flow needs a way for the requester / HR team to
  // amend and resubmit). Every other request type is immutable once
  // submitted — direct navigation to /hr-requests/{id}/edit is blocked
  // here so the form can't be opened for them.
  if (
    isEditing
    && loadedRequest
    && String(loadedRequest?.request_type || '').toLowerCase() !== 'pre_approval'
  ) {
    return (
      <ForbiddenScreen
        title="Editing not available"
        message="This request type can't be edited after submission."
        hint="Only Pre-Approval requests support an edit + resubmit flow. Open the request details to view it or submit a new request if changes are needed."
      />
    );
  }

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        variant="dashboard"
        overline="Requests"
        title={isEditing ? `Edit ${workflowTitle}` : 'Create HR Request'}
        subtitle={
          isEditing
            ? 'Update the request and resubmit it through the existing approval flow.'
            : 'Choose a request type, complete the required details, and review the approval route before submitting.'
        }
        actions={[
          <Button
            key="back"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/requests/new')}
          >
            Request Catalog
          </Button>,
        ]}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      {isPreApprovalRevision ? (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              This request was returned to you for revision.
            </Typography>
            <Typography variant="body2">
              <strong>Requested By:</strong>{' '}
              {loadedRequest?.pre_approval_revision_requested_by_name ||
                loadedRequest?.pre_approval_revision_requested_by_label ||
                loadedRequest?.pre_approval_revision_requested_by_step ||
                'Approver'}
            </Typography>
            <Typography variant="body2">
              <strong>Returned To:</strong> {revisionTargetDisplay}
            </Typography>
            <Box
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'warning.light',
                backgroundColor: 'rgba(255, 193, 7, 0.08)',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700 }}
              >
                Revision Reason
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', fontWeight: 600 }}>
                {loadedRequest?.pre_approval_revision_reason || 'No revision reason was provided.'}
              </Typography>
            </Box>
          </Stack>
        </Alert>
      ) : null}

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        <Chip
          color="primary"
          icon={<InfoOutlinedIcon />}
          label={workflowTitle}
          sx={{ fontWeight: 700 }}
        />
        <Chip
          variant="outlined"
          icon={<RouteRoundedIcon />}
          label={workflowModeLabel}
          sx={{ fontWeight: 700 }}
        />
        <Chip
          variant="outlined"
          label={isEditing ? 'Edit mode' : 'Create mode'}
          sx={{ fontWeight: 700 }}
        />
        {validationErrorCount > 0 ? (
          <Chip
            color="error"
            label={`${validationErrorCount} ${validationErrorCount === 1 ? 'field needs' : 'fields need'} attention`}
            sx={{ fontWeight: 700 }}
          />
        ) : null}
      </Stack>

      <SectionCard
        tone="dashboard"
        title="Request Details"
        subheader="Select a type, then fill the required fields. Optional metadata helps reviewers act faster."
        disablePadding
        contentSx={{ p: 0 }}
      >
        <Box component="form" onSubmit={handleSubmit} sx={formShellSx}>
          <Box sx={requestIntroSx}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              sx={{ mb: 1.2 }}
            >
              <Box>
                <Typography variant="overline" color="primary" sx={{ fontWeight: 800, letterSpacing: '0.08em' }}>
                  Request setup
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  Start with the right request type
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  The selected type controls the required fields, available attachments, and approval path.
                </Typography>
              </Box>
              <Chip
                size="small"
                label={isEditing ? 'Type locked while editing' : 'Type can be changed before submit'}
                color={isEditing ? 'default' : 'primary'}
                variant={isEditing ? 'outlined' : 'filled'}
                sx={{ fontWeight: 700 }}
              />
            </Stack>

            {/* Request Type */}
            <FormControl fullWidth>
            <InputLabel id="request_type-label">Request Type</InputLabel>
            <Select
              labelId="request_type-label"
              label="Request Type"
              name="request_type"
              value={request_type}
              onChange={handleChange}
              disabled={isEditing}
              error={Boolean(getFieldError('request_type'))}
            >
              <MenuItem value="to_leave">Request to Leave</MenuItem>
              <MenuItem value="loans">Loans</MenuItem>
              <MenuItem value="finance_claim">Finance Claim</MenuItem>
              <MenuItem value="misc">Miscellaneous</MenuItem>
              <MenuItem value="business_trip">Business Trip</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
              <MenuItem value="resignation">Resignation</MenuItem>
              <MenuItem value="personal_data_change">Personal Data Change</MenuItem>
              <MenuItem value="missing_punch">Missing Punch</MenuItem>
              {canSelectPreApproval || (isEditing && request_type === 'pre_approval') ? (
                <MenuItem value="pre_approval">Pre-Approval Request</MenuItem>
              ) : null}
            </Select>
            {getFieldError('request_type') && (
              <Typography variant="caption" color="error">
                {getFieldError('request_type')}
              </Typography>
            )}
            </FormControl>
          </Box>

          <Box sx={formGridSx}>
            <Box sx={{ minWidth: 0, px: { xs: 1.35, sm: 1.7 }, pb: { xs: 1.35, sm: 1.7 } }}>

          <Box sx={{ mb: 1.4 }}>
            {hasPublishedWorkflowEngine && !isEditing ? (
              <WorkflowPathPreview
                title={`${workflowTitle} Workflow`}
                subheader="Live path preview from the published workflow and your current request values."
                workflow={workflowPreview || selectedWorkflowGuide}
                simulation={workflowPreview?.simulation}
                loading={workflowPreviewLoading}
                error={workflowPreviewError}
              />
            ) : (
              <WorkflowGuideCard
                title={`${workflowTitle} Workflow`}
                subheader="Clear approval path for this request type."
                steps={workflowSteps}
              />
            )}
          </Box>

          {/* Optional common metadata */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mb: 1.4 }}>
            <FormControl fullWidth>
              <InputLabel id="priority-label">Priority (Optional)</InputLabel>
              <Select
                labelId="priority-label"
                label="Priority (Optional)"
                name="priority"
                value={formValues.priority}
                onChange={handleChange}
                error={Boolean(getFieldError('priority'))}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
              {getFieldError('priority') && (
                <Typography variant="caption" color="error">
                  {getFieldError('priority')}
                </Typography>
              )}
            </FormControl>

            <TextField
              name="due_date"
              label="Due Date (Optional)"
              type="date"
              value={formValues.due_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              error={Boolean(getFieldError('due_date'))}
              helperText={getFieldError('due_date')}
            />
          </Stack>

          <TextField
            name="notes"
            label="Reviewer notes (Optional)"
            value={formValues.notes}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={3}
            sx={{ mb: 2 }}
            error={Boolean(getFieldError('notes'))}
            helperText={getFieldError('notes') || 'Visible to the manager and HR reviewer when they look at this request.'}
          />

          <Divider sx={{ my: 2 }} />

          {/* ---------- to_leave ---------- */}
          {request_type === 'to_leave' && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Leave Type</InputLabel>
                <Select
                  name="leaveType"
                  value={formValues.leaveType}
                  label="Leave Type"
                  onChange={handleChange}
                  error={Boolean(getFieldError('leaveType'))}
                >
                  <MenuItem value="personal">Personal</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="work_leave">Work Leave</MenuItem>
                </Select>
                {getFieldError('leaveType') && (
                  <Typography variant="caption" color="error">
                    {getFieldError('leaveType')}
                  </Typography>
                )}
              </FormControl>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                <TextField
                  name="fromDate"
                  label="From Date"
                  type="date"
                  value={formValues.fromDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={Boolean(getFieldError('fromDate'))}
                  helperText={getFieldError('fromDate')}
                />
                <TextField
                  name="fromTime"
                  label="From Time"
                  type="time"
                  value={formValues.fromTime}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={Boolean(getFieldError('fromTime'))}
                  helperText={getFieldError('fromTime')}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                <TextField
                  name="toDate"
                  label="To Date"
                  type="date"
                  value={formValues.toDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={Boolean(getFieldError('toDate'))}
                  helperText={getFieldError('toDate')}
                />
                <TextField
                  name="toTime"
                  label="To Time"
                  type="time"
                  value={formValues.toTime}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={Boolean(getFieldError('toTime'))}
                  helperText={getFieldError('toTime')}
                />
              </Box>

              <TextField
                name="reason"
                label="Reason"
                value={formValues.reason}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('reason'))}
                helperText={getFieldError('reason')}
              />
            </Box>
          )}

          {/* ---------- loans ---------- */}
          {request_type === 'loans' && (
            <Box sx={{ mb: 2 }}>
              <TextField
                name="loanDate"
                label="Date"
                type="date"
                value={formValues.loanDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('loanDate'))}
                helperText={getFieldError('loanDate')}
              />
              <TextField
                name="noOfPayments"
                label="Number of Payments"
                type="number"
                value={formValues.noOfPayments}
                onChange={handleChange}
                fullWidth
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('noOfPayments'))}
                helperText={getFieldError('noOfPayments')}
              />
              <TextField
                name="loanAmount"
                label="Amount"
                type="number"
                value={formValues.loanAmount}
                onChange={handleChange}
                fullWidth
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('loanAmount'))}
                helperText={getFieldError('loanAmount')}
              />
              <TextField
                name="loanReason"
                label="Reason"
                value={formValues.loanReason}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('loanReason'))}
                helperText={getFieldError('loanReason')}
              />
            </Box>
          )}

          {/* ---------- finance_claim ---------- */}
          {request_type === 'finance_claim' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Expense Items
              </Typography>
              <Box
                sx={{
                  overflowX: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 1.5,
                }}
              >
              <Table size="small" sx={{ minWidth: 920 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Transaction Date</TableCell>
                    <TableCell>Expense Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Attachment</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formValues.financeClaims.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField
                          type="date"
                          name={`financeClaims-${idx}-transaction_date`}
                          value={item.transaction_date}
                          onChange={(e) => handleChange(e, idx, 'transaction_date')}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          name={`financeClaims-${idx}-expense_type`}
                          value={item.expense_type}
                          onChange={(e) => handleChange(e, idx, 'expense_type')}
                          size="small"
                          placeholder="e.g. Travel, Meal"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          name={`financeClaims-${idx}-amount`}
                          value={item.amount}
                          onChange={(e) => handleChange(e, idx, 'amount')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          name={`financeClaims-${idx}-notes`}
                          value={item.notes}
                          onChange={(e) => handleChange(e, idx, 'notes')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button variant="outlined" component="label" size="small">
                          Upload
                          <input
                            type="file"
                            hidden
                            onChange={(e) => handleChange(e, idx, 'attachment')}
                          />
                        </Button>
                        {item.attachment && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {item.attachment.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {formValues.financeClaims.length > 1 && (
                          <IconButton color="error" onClick={() => removeFinanceClaimRow(idx)}>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </Box>

              <Box sx={{ mt: 1 }}>
                <Button type="button" startIcon={<AddIcon />} onClick={addFinanceClaimRow}>
                  Add Expense Row
                </Button>
              </Box>
            </Box>
          )}

          {/* ---------- resignation ---------- */}
          {request_type === 'resignation' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Resignation Details
              </Typography>
              <Box
                sx={{
                  overflowX: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Resignation Date</TableCell>
                    <TableCell>Last Working Day</TableCell>
                    <TableCell>Notice Period</TableCell>
                    <TableCell>Resignation Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <TextField
                        type="date"
                        name="resignation_date"
                        value={formValues.resignation_date}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        error={Boolean(getFieldError('resignation_date'))}
                        helperText={getFieldError('resignation_date')}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="date"
                        name="last_working_day"
                        value={formValues.last_working_day}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        error={Boolean(getFieldError('last_working_day'))}
                        helperText={getFieldError('last_working_day')}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        name="notice_period"
                        value={formValues.notice_period}
                        onChange={handleChange}
                        size="small"
                        error={Boolean(getFieldError('notice_period'))}
                        helperText={getFieldError('notice_period')}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        name="resignation_reason"
                        value={formValues.resignation_reason}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        error={Boolean(getFieldError('resignation_reason'))}
                        helperText={getFieldError('resignation_reason')}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              </Box>
            </Box>
          )}

          {/* ---------- misc ---------- */}
          {request_type === 'misc' && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Requested Item</InputLabel>
                <Select
                  name="requestedItem"
                  label="Requested Item"
                  value={formValues.requestedItem}
                  onChange={handleChange}
                  error={Boolean(getFieldError('requestedItem'))}
                >
                  <MenuItem value="laptop_it">Laptop (IT Request)</MenuItem>
                  <MenuItem value="mobile_phone">Mobile Phone</MenuItem>
                  <MenuItem value="mobile_sim">Mobile SIM Card</MenuItem>
                  <MenuItem value="profession_change">Profession Change</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
                {getFieldError('requestedItem') && (
                  <Typography variant="caption" color="error">
                    {getFieldError('requestedItem')}
                  </Typography>
                )}
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.isTemporary}
                    onChange={handleCheckboxChange}
                    name="isTemporary"
                  />
                }
                label="Temporary?"
                sx={{ mb: 2 }}
              />

              {formValues.isTemporary && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                    <TextField
                      name="miscFromDate"
                      label="From Date"
                      type="date"
                      value={formValues.miscFromDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={Boolean(getFieldError('miscFromDate'))}
                      helperText={getFieldError('miscFromDate')}
                    />
                    <TextField
                      name="miscFromTime"
                      label="From Time"
                      type="time"
                      value={formValues.miscFromTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={Boolean(getFieldError('miscFromTime'))}
                      helperText={getFieldError('miscFromTime')}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                    <TextField
                      name="miscToDate"
                      label="To Date"
                      type="date"
                      value={formValues.miscToDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={Boolean(getFieldError('miscToDate'))}
                      helperText={getFieldError('miscToDate')}
                    />
                    <TextField
                      name="miscToTime"
                      label="To Time"
                      type="time"
                      value={formValues.miscToTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={Boolean(getFieldError('miscToTime'))}
                      helperText={getFieldError('miscToTime')}
                    />
                  </Box>
                </Box>
              )}

              <TextField
                name="miscReason"
                label="Reason"
                value={formValues.miscReason}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('miscReason'))}
                helperText={getFieldError('miscReason')}
              />

              <TextField
                name="miscNotes"
                label="Additional details for this miscellaneous request"
                value={formValues.miscNotes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('miscNotes'))}
                helperText={getFieldError('miscNotes')}
              />
            </Box>
          )}

          {/* ---------- bank ---------- */}
          {request_type === 'bank' && (
            <Box sx={{ mb: 2 }}>
              <TextField
                name="bankTransactionDate"
                label="Transaction Date"
                type="date"
                value={formValues.bankTransactionDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('bankTransactionDate'))}
                helperText={getFieldError('bankTransactionDate')}
              />
              <TextField
                name="bankName"
                label="Bank Name"
                value={formValues.bankName}
                onChange={handleChange}
                fullWidth
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('bankName'))}
                helperText={getFieldError('bankName')}
              />
              <TextField
                name="bankBranch"
                label="Branch"
                value={formValues.bankBranch}
                onChange={handleChange}
                fullWidth
                sx={{ mb: 2 }}
                placeholder="main"
                error={Boolean(getFieldError('bankBranch'))}
                helperText={getFieldError('bankBranch')}
              />
              <TextField
                name="newAccountNumber"
                label="New Account Number"
                value={formValues.newAccountNumber}
                onChange={handleChange}
                fullWidth
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('newAccountNumber'))}
                helperText={getFieldError('newAccountNumber')}
              />
              <TextField
                name="newIbanNumber"
                label="New IBAN Number"
                value={formValues.newIbanNumber}
                onChange={handleChange}
                fullWidth
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('newIbanNumber'))}
                helperText={getFieldError('newIbanNumber')}
              />
              <TextField
                name="swiftCode"
                label="SWIFT Code"
                value={formValues.swiftCode}
                onChange={handleChange}
                fullWidth
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('swiftCode'))}
                helperText={getFieldError('swiftCode')}
              />
              <TextField
                name="bankNotes"
                label="Additional details for this bank request"
                value={formValues.bankNotes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('bankNotes'))}
                helperText={getFieldError('bankNotes')}
              />
            </Box>
          )}

          {/* ---------- personal_data_change ---------- */}
          {request_type === 'personal_data_change' && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Field to Change</InputLabel>
                <Select
                  name="personalDataField"
                  label="Field to Change"
                  value={formValues.personalDataField}
                  onChange={handleChange}
                  error={Boolean(getFieldError('personalDataField'))}
                >
                  {pdOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {getFieldError('personalDataField') && (
                  <Typography variant="caption" color="error">
                    {getFieldError('personalDataField')}
                  </Typography>
                )}
              </FormControl>

              <TextField
                name="personalDataValue"
                label="New Value"
                value={formValues.personalDataValue}
                onChange={handleChange}
                fullWidth
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('personalDataValue'))}
                helperText={getFieldError('personalDataValue')}
              />

              <TextField
                name="description"
                label="Reason / Notes (optional)"
                value={formValues.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('description'))}
                helperText={getFieldError('description')}
              />
            </Box>
          )}

          {/* ---------- business_trip ---------- */}
          {request_type === 'business_trip' && (
            <Box sx={{ mb: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <FormControl fullWidth error={Boolean(getFieldError('business_trip_ticket_demand'))}>
                  <InputLabel>Ticket / Accommodation</InputLabel>
                  <Select
                    name="business_trip_ticket_demand"
                    label="Ticket / Accommodation"
                    value={formValues.business_trip_ticket_demand}
                    onChange={handleChange}
                  >
                    {businessTripTicketDemandOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                  {getFieldError('business_trip_ticket_demand') && (
                    <Typography variant="caption" color="error">
                      {getFieldError('business_trip_ticket_demand')}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth error={Boolean(getFieldError('business_trip_type'))}>
                  <InputLabel>Trip Type</InputLabel>
                  <Select
                    name="business_trip_type"
                    label="Trip Type"
                    value={formValues.business_trip_type}
                    onChange={handleChange}
                  >
                    {businessTripTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                  {getFieldError('business_trip_type') && (
                    <Typography variant="caption" color="error">
                      {getFieldError('business_trip_type')}
                    </Typography>
                  )}
                </FormControl>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <FormControl fullWidth sx={{ mb: 2 }} error={Boolean(getFieldError('busTripRegion'))}>
                  <InputLabel>Region</InputLabel>
                  <Select
                    name="busTripRegion"
                    label="Region"
                    value={formValues.busTripRegion}
                    onChange={handleChange}
                  >
                    <MenuItem value="gcc">GCC</MenuItem>
                    <MenuItem value="non_gcc">Non-GCC</MenuItem>
                  </Select>
                  {getFieldError('busTripRegion') && (
                    <Typography variant="caption" color="error">
                      {getFieldError('busTripRegion')}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth error={Boolean(getFieldError('business_trip_destination'))}>
                  <InputLabel>Destination</InputLabel>
                  <Select
                    name="business_trip_destination"
                    label="Destination"
                    value={formValues.business_trip_destination}
                    onChange={handleChange}
                  >
                    {businessTripDestinationOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                  {getFieldError('business_trip_destination') && (
                    <Typography variant="caption" color="error">
                      {getFieldError('business_trip_destination')}
                    </Typography>
                  )}
                </FormControl>
              </Stack>

              <FormControl fullWidth sx={{ mb: 2 }} error={Boolean(getFieldError('business_trip_travel_method'))}>
                <InputLabel>Travel Method</InputLabel>
                <Select
                  name="business_trip_travel_method"
                  label="Travel Method"
                  value={formValues.business_trip_travel_method}
                  onChange={handleChange}
                >
                  {businessTripTravelMethodOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
                {getFieldError('business_trip_travel_method') && (
                  <Typography variant="caption" color="error">
                    {getFieldError('business_trip_travel_method')}
                  </Typography>
                )}
              </FormControl>

              <TextField
                name="busTripReason"
                label="Reason"
                value={formValues.busTripReason}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('busTripReason'))}
                helperText={getFieldError('busTripReason')}
              />

              <TextField
                name="busTripNotes"
                label="Additional details for this business trip request"
                value={formValues.busTripNotes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                error={Boolean(getFieldError('busTripNotes'))}
                helperText={getFieldError('busTripNotes')}
              />

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                <TextField
                  name="fromDate"
                  label="From Date"
                  type="date"
                  value={formValues.fromDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={Boolean(getFieldError('fromDate'))}
                  helperText={getFieldError('fromDate')}
                />
                <TextField
                  name="fromTime"
                  label="From Time"
                  type="time"
                  value={formValues.fromTime}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={Boolean(getFieldError('fromTime'))}
                  helperText={getFieldError('fromTime')}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                <TextField
                  name="toDate"
                  label="To Date"
                  type="date"
                  value={formValues.toDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={Boolean(getFieldError('toDate'))}
                  helperText={getFieldError('toDate')}
                />
                <TextField
                  name="toTime"
                  label="To Time"
                  type="time"
                  value={formValues.toTime}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={Boolean(getFieldError('toTime'))}
                  helperText={getFieldError('toTime')}
                />
              </Box>

              {/* Multiple attachments */}
              <Box sx={{ mb: 2 }}>
                <Button variant="outlined" component="label">
                  Upload Attachments
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleBusinessTripFiles}
                  />
                </Button>
                {formValues.businessTripAttachments.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {formValues.businessTripAttachments.map((file, index) => (
                      <Typography key={index} variant="body2">
                        {file.name}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* ---------- missing_punch ---------- */}
          {request_type === 'missing_punch' && (
            <Box sx={{ mb: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  name="missing_punch_date"
                  label="Punch Date"
                  type="date"
                  value={formValues.missing_punch_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={Boolean(getFieldError('missing_punch_date'))}
                  helperText={getFieldError('missing_punch_date')}
                />
                <TextField
                  name="missing_punch_time"
                  label="Punch Time"
                  type="time"
                  value={formValues.missing_punch_time}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={Boolean(getFieldError('missing_punch_time'))}
                  helperText={getFieldError('missing_punch_time')}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <FormControl fullWidth error={Boolean(getFieldError('missing_punch_type'))}>
                  <InputLabel>Punch Type</InputLabel>
                  <Select
                    name="missing_punch_type"
                    label="Punch Type"
                    value={formValues.missing_punch_type}
                    onChange={handleChange}
                  >
                    <MenuItem value="check_in">Check In</MenuItem>
                    <MenuItem value="check_out">Check Out</MenuItem>
                  </Select>
                  {getFieldError('missing_punch_type') && (
                    <Typography variant="caption" color="error">
                      {getFieldError('missing_punch_type')}
                    </Typography>
                  )}
                </FormControl>

                <Autocomplete
                  fullWidth
                  options={projects}
                  value={selectedMissingPunchProject}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  onChange={(_, option) => {
                    setFormValues((prev) => ({
                      ...prev,
                      missing_punch_project_id: option ? String(option.id) : '',
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Project (optional)"
                      error={Boolean(getFieldError('missing_punch_project_id'))}
                      helperText={getFieldError('missing_punch_project_id') || 'Search the project list when this punch belongs to a project site.'}
                    />
                  )}
                />
              </Stack>

              <TextField
                name="description"
                label="Reason"
                value={formValues.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                error={Boolean(getFieldError('description'))}
                helperText={getFieldError('description') || 'Explain why the attendance punch is missing.'}
              />
            </Box>
          )}

          {/* ---------- pre_approval ---------- */}
          {request_type === 'pre_approval' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700 }}>
                Pre-Approval Form for Employment Offer
              </Typography>

              <Stack spacing={2}>
                <TextField
                  name="candidate_name"
                  label="Name of Candidate"
                  value={formValues.candidate_name}
                  onChange={handleChange}
                  fullWidth
                  error={Boolean(getFieldError('candidate_name'))}
                  helperText={getFieldError('candidate_name')}
                />
                <TextField
                  name="position_offered"
                  label="Position Offered"
                  value={formValues.position_offered}
                  onChange={handleChange}
                  fullWidth
                  error={Boolean(getFieldError('position_offered'))}
                  helperText={getFieldError('position_offered')}
                />
                <TextField
                  name="project_client"
                  label="Project / Client"
                  value={formValues.project_client}
                  onChange={handleChange}
                  fullWidth
                  error={Boolean(getFieldError('project_client'))}
                  helperText={getFieldError('project_client')}
                />
                <FormControl fullWidth error={Boolean(getFieldError('pre_approval_department_id'))}>
                  <InputLabel id="pre-approval-department-label">Department</InputLabel>
                  <Select
                    labelId="pre-approval-department-label"
                    name="pre_approval_department_id"
                    label="Department"
                    value={formValues.pre_approval_department_id}
                    onChange={handleChange}
                  >
                    <MenuItem value="">Select Department</MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={String(dept.id)}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color={getFieldError('pre_approval_department_id') ? 'error' : 'text.secondary'}>
                    {getFieldError('pre_approval_department_id') || preApprovalDepartmentHint}
                  </Typography>
                </FormControl>
                {formValues.pre_approval_department_id ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    {preApprovalApproverMessage}
                  </Alert>
                ) : null}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    name="package_salary"
                    label="Package Salary"
                    type="number"
                    value={formValues.package_salary}
                    onChange={handleChange}
                    fullWidth
                    error={Boolean(getFieldError('package_salary'))}
                    helperText={getFieldError('package_salary')}
                  />
                  <TextField
                    name="other_cost"
                    label="Other Cost"
                    type="number"
                    value={formValues.other_cost}
                    onChange={handleChange}
                    fullWidth
                    error={Boolean(getFieldError('other_cost'))}
                    helperText={getFieldError('other_cost')}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    name="total_cost_to_company"
                    label="Total Cost to Company"
                    type="number"
                    value={formValues.total_cost_to_company}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    error={Boolean(getFieldError('total_cost_to_company'))}
                    helperText={getFieldError('total_cost_to_company') || 'Auto-calculated as package salary + other cost.'}
                  />
                </Stack>

                {canEditPreApprovalCommercialFields ? (
                  <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <FormControl fullWidth error={Boolean(getFieldError('pre_approval_billing_mode'))}>
                        <InputLabel id="pre-approval-billing-mode-label">Billing Mode</InputLabel>
                        <Select
                          labelId="pre-approval-billing-mode-label"
                          name="pre_approval_billing_mode"
                          label="Billing Mode"
                          value={formValues.pre_approval_billing_mode}
                          onChange={handleChange}
                        >
                          <MenuItem value="billed">Billed</MenuItem>
                          <MenuItem value="lumpsum">Lumpsum</MenuItem>
                          <MenuItem value="billing_hq">Billing HQ</MenuItem>
                        </Select>
                        <Typography variant="caption" color={getFieldError('pre_approval_billing_mode') ? 'error' : 'text.secondary'}>
                          {getFieldError('pre_approval_billing_mode') || 'Commercial review fields are required for the approver handling billing.'}
                        </Typography>
                      </FormControl>

                      <TextField
                        name="client_billing"
                        label={preApprovalBillingFieldLabel}
                        type={preApprovalIsBillingHq ? 'text' : 'number'}
                        value={preApprovalIsBillingHq ? 'N/A' : formValues.client_billing}
                        onChange={handleChange}
                        fullWidth
                        error={!preApprovalIsBillingHq && Boolean(getFieldError('client_billing'))}
                        helperText={preApprovalIsBillingHq ? 'Not applicable for Billing HQ.' : getFieldError('client_billing')}
                        InputProps={{ readOnly: preApprovalIsBillingHq }}
                      />
                    </Stack>

                    <TextField
                      name="marginal_benefit"
                      label="Margin Benefit %"
                      type={preApprovalIsBillingHq ? 'text' : 'number'}
                      value={preApprovalIsBillingHq ? 'N/A' : formValues.marginal_benefit}
                      onChange={handleChange}
                      fullWidth
                      InputProps={{ readOnly: preApprovalIsBillingHq || formValues.pre_approval_billing_mode === 'billed' }}
                      helperText={
                        (preApprovalIsBillingHq
                          ? 'Not applicable for Billing HQ.'
                          : getFieldError('marginal_benefit')) ||
                        (formValues.pre_approval_billing_mode === 'billed'
                          ? 'Auto-calculated from total cost to company and client billing.'
                          : 'Enter the margin benefit percentage for the lumpsum case.')
                      }
                      error={!preApprovalIsBillingHq && Boolean(getFieldError('marginal_benefit'))}
                    />
                  </>
                ) : null}
                <TextField
                  name="conditional_terms"
                  label="Conditional Terms"
                  value={formValues.conditional_terms}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={2}
                  error={Boolean(getFieldError('conditional_terms'))}
                  helperText={getFieldError('conditional_terms')}
                />
                <TextField
                  name="conflict_of_interest"
                  label="Conflict of Interest"
                  value={formValues.conflict_of_interest}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={2}
                  error={Boolean(getFieldError('conflict_of_interest'))}
                  helperText={getFieldError('conflict_of_interest')}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    name="requester_name"
                    label="Requester"
                    value={formValues.requester_name}
                    onChange={handleChange}
                    fullWidth
                    error={Boolean(getFieldError('requester_name'))}
                    helperText={getFieldError('requester_name')}
                  />
                  <TextField
                    name="prepared_by_name"
                    label="Prepared By"
                    value={formValues.prepared_by_name}
                    onChange={handleChange}
                    fullWidth
                    error={Boolean(getFieldError('prepared_by_name'))}
                    helperText={getFieldError('prepared_by_name')}
                  />
                </Stack>
              </Stack>
            </Box>
          )}

          {/* ---------- Single Attachment (common to all) ---------- */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 3,
              border: '1px dashed',
              borderColor: alpha(theme.palette.primary.main, 0.35),
              backgroundColor: alpha(theme.palette.primary.main, 0.035),
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Button variant="outlined" component="label" startIcon={<AttachmentOutlinedIcon />}>
                {attachmentButtonLabel}
                <input
                  type="file"
                  hidden
                  name="attachment"
                  onChange={handleChange}
                />
              </Button>
              {formValues.attachment ? (
                <Chip
                  icon={<AttachmentOutlinedIcon />}
                  label={formValues.attachment.name}
                  variant="outlined"
                  sx={{
                    maxWidth: { xs: '100%', sm: 320 },
                    justifyContent: 'flex-start',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: 'block' }}>
              {attachmentHelperText}
            </Typography>
          </Box>
            </Box>

            <Box sx={summaryPanelSx}>
              <Stack spacing={1.2}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      color: 'primary.main',
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    }}
                  >
                    <RouteRoundedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      Request summary
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Review before submitting
                    </Typography>
                  </Box>
                </Stack>

                <Divider />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: '1fr' },
                    gap: 0.85,
                  }}
                >
                {summaryRows.map((row) => (
                  <Box key={row.label} sx={summaryRowSx}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
                </Box>

                <Alert
                  severity={validationErrorCount > 0 ? 'warning' : 'info'}
                  sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
                >
                  {validationErrorCount > 0
                    ? 'Fix the highlighted fields, then submit again.'
                    : 'Check the workflow and attachment before submitting.'}
                </Alert>
              </Stack>
            </Box>
          </Box>

          <Box sx={stickyActionBarSx}>
            <Button
              type="button"
              variant="text"
              onClick={() => navigate('/requests/new')}
              disabled={loading || bootstrapping}
            >
              Request catalog
            </Button>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button
                type="button"
                variant="outlined"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                disabled={loading || bootstrapping}
              >
                Review top
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveRoundedIcon />}
                disabled={loading || bootstrapping}
              >
                {submitLabel}
              </Button>
            </Stack>
          </Box>
        </Box>
      </SectionCard>
    </PageLayout>
  );
}

export default NewHrRequestForm;
